/**
 * The site page's response time chart: every check still kept (45 days),
 * zoomable from half an hour to the whole history and scrollable through
 * it, with the outages painted in.
 *
 * What sits where, top to bottom:
 *
 *  - The window line ("Last 24 h", "Yesterday 20:00 - today 02:00") with a
 *    spinner while tiles for it load, and the zoom buttons.
 *  - The frame: a native horizontal scroller (`useChartViewport`) around the
 *    plot (`ResponsePlot`), the status lane and the edge fades, which sit in
 *    the plot's box under its tooltip. Pinch, Ctrl + wheel and mouse drag are
 *    `useChartGestures`; the keyboard is the viewport's.
 *  - The figures for exactly what is on screen (`ViewStatsRow`).
 *  - The navigator: the whole history in one strip, with the window on it.
 *  - What the marks and the lane's colours mean (`ChartLegend`).
 *
 * Data arrives in tiles (`useSeriesTiles`) at the step the zoom and the
 * plot's width call for (`chooseStep`). While a new step's tiles load, the
 * frame keeps drawing the last step that covered the window completely, at
 * reduced opacity, so a zoom never flashes an empty chart; a scroll into
 * history not yet read draws what is in hand, dimmed, until the rest lands.
 *
 * `liveKey` is the site's last check time from the page's minute poll. When
 * it changes, "now" moves to it (never behind it, so a browser clock a
 * minute slow does not hide the newest check), a chart that follows now
 * moves along, and the tiles that can still gain checks are read again.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { cn } from "@wa/ui";
import {
  DEFAULT_SPAN_MS,
  chartRows,
  chooseStep,
  historyRange,
  laneSegments,
  viewSeries,
  viewStats,
} from "../../../../lib/monitoring";
import { Btn } from "../../../ui";
import type { T } from "../words";
import { yScale } from "./axis";
import { ChartLegend } from "./ChartLegend";
import { ChartNavigator } from "./ChartNavigator";
import { EdgeFade } from "./EdgeFade";
import { windowText } from "./labels";
import { PLOT_LEFT, PLOT_RIGHT } from "./layout";
import { ResponsePlot } from "./ResponsePlot";
import { StatusLane } from "./StatusLane";
import { useChartGestures } from "./useChartGestures";
import { useChartViewport } from "./useChartViewport";
import { useSeriesTiles } from "./useSeriesTiles";
import { ViewStatsRow } from "./ViewStats";
import { ZoomControls } from "./ZoomControls";

interface Props {
  monitorId: string;
  /** The site's check interval: how dense raw checks are, and how wide one failure is drawn. */
  checkMinutes: number;
  /** The oldest kept check (ISO), null when the site has none. */
  historyFrom: string | null;
  /** The site's last check time (null: never checked); a change means new data to show. */
  liveKey: string | null;
  t: T;
}

export function ResponseChart(props: Props) {
  if (props.historyFrom === null) {
    // No kept check: never checked at all, or every check is older than the 45 days
    // raw checks are kept (a site paused that long). The page's own "last check" line
    // shows a date in the second case, so this must not say it was never checked.
    const key = props.liveKey === null ? "monitoring.chart.empty" : "monitoring.chart.pruned";
    return <p className="crm-mut py-10 text-center text-[13px]">{props.t(key)}</p>;
  }
  return <Chart {...props} historyFrom={props.historyFrom} />;
}

/** Now, or the last check's time if the browser's clock is behind the server's. */
function liveNow(liveKey: string | null): number {
  const last = liveKey ? Date.parse(liveKey) : Number.NaN;
  return Math.max(Date.now(), Number.isFinite(last) ? last + 1 : 0);
}

/** The history's end: set when the chart mounts and moved each time a new check lands. */
function useLiveNow(liveKey: string | null): number {
  const [now, setNow] = useState(() => liveNow(liveKey));
  const seen = useRef(liveKey);
  useEffect(() => {
    if (seen.current === liveKey) return;
    seen.current = liveKey;
    setNow(liveNow(liveKey));
  }, [liveKey]);
  return now;
}

function Chart({ monitorId, checkMinutes, historyFrom, liveKey, t }: Props & { historyFrom: string }) {
  const now = useLiveNow(liveKey);
  const range = useMemo(() => historyRange(historyFrom, now), [historyFrom, now]);
  const vp = useChartViewport(range);
  useChartGestures(vp);

  const { from, span, pinned, plotWidth } = vp;
  const to = from + span;
  const step = chooseStep(span, plotWidth, checkMinutes);
  // Half a window each side is read ahead, so a scroll usually finds its tiles in hand.
  const margin = span / 2;
  const want = plotWidth > 0 ? { step, from: Math.max(range.start, from - margin), to: Math.min(range.end, to + margin) } : null;
  const { cover, retry } = useSeriesTiles(monitorId, want, liveKey, checkMinutes);

  // The step the frame last drew completely, to hold on to while a new step loads.
  const [drawnStep, setDrawnStep] = useState(step);
  const main = useMemo(() => cover(step, from, to, margin), [cover, step, from, to, margin]);
  useEffect(() => {
    if (main.complete && drawnStep !== step) setDrawnStep(step);
  }, [main.complete, drawnStep, step]);

  const shown = useMemo(() => {
    if (main.complete || drawnStep === step) return { step, cover: main };
    const held = cover(drawnStep, from, to, margin);
    return held.complete || held.tiles.length > main.tiles.length ? { step: drawnStep, cover: held } : { step, cover: main };
  }, [main, cover, drawnStep, step, from, to, margin]);

  const drawn = useMemo(() => {
    const { points, downSpans } = viewSeries(shown.cover.tiles, from, to);
    return {
      rows: chartRows(points, shown.step, checkMinutes),
      downSpans,
      lane: laneSegments(points, shown.step, checkMinutes, from, to),
      stats: viewStats(points, downSpans, shown.step, from, to),
      y: yScale(points, shown.step, from, to),
    };
  }, [shown, from, to, checkMinutes]);

  const canScroll = vp.geo.maxScroll > 0;
  const noAnswer = main.complete && drawn.stats.checks > 0 && drawn.stats.median === null;
  const measured = vp.width > 0;

  return (
    <div>
      <div className="mb-2 flex flex-wrap items-center gap-x-3 gap-y-2">
        <p className="flex min-w-0 items-center gap-2 text-[13px] font-semibold">
          <span className="crm-tnum">{windowText(t, from, to, now, pinned)}</span>
          {main.loading ? (
            <span className="crm-mut inline-flex items-center gap-1.5 text-[12px] font-normal">
              <Loader2 aria-hidden className="size-3.5 animate-spin" />
              {t("monitoring.chart.loading")}
            </span>
          ) : null}
        </p>
        <ZoomControls
          span={span}
          pinned={pinned}
          canZoomIn={vp.canZoomIn}
          canZoomOut={vp.canZoomOut}
          onZoomIn={vp.zoomIn}
          onZoomOut={vp.zoomOut}
          onNow={vp.toNow}
          t={t}
        />
      </div>

      {main.failed ? (
        <div className="mb-2 flex flex-wrap items-center gap-3 text-[13px] text-[var(--color-destructive)]">
          <span>{t("monitoring.chart.failed")}</span>
          <Btn small onClick={retry}>
            {t("monitoring.retry")}
          </Btn>
        </div>
      ) : null}

      <div
        ref={vp.scrollerRef}
        tabIndex={0}
        role="region"
        aria-label={t("monitoring.chart.frame")}
        onScroll={vp.onScroll}
        onKeyDown={vp.onKeyDown}
        className={cn(
          // recharts puts `cursor: default` inline on its wrapper; the frame's grab cursor has to win over it.
          "overflow-x-auto overflow-y-hidden rounded-lg outline-none [scrollbar-width:none] focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] [&::-webkit-scrollbar]:hidden [&_.recharts-wrapper]:cursor-[inherit]!",
          canScroll && "cursor-grab data-[dragging]:cursor-grabbing",
        )}
        style={{ touchAction: "pan-x pan-y", overscrollBehaviorX: "contain" }}
      >
        <div style={{ width: measured ? vp.geo.contentWidth : "100%" }}>
          <div className="sticky left-0 select-none" style={{ width: measured ? vp.width : "100%" }}>
            <div className="relative transition-opacity duration-200" style={{ opacity: main.complete ? 1 : 0.55 }}>
              <ResponsePlot
                rows={drawn.rows}
                downSpans={drawn.downSpans}
                from={from}
                span={span}
                step={shown.step}
                checkMinutes={checkMinutes}
                plotWidth={plotWidth}
                y={drawn.y}
                pinned={pinned}
                t={t}
              />
              <StatusLane segments={drawn.lane} from={from} span={span} />
              {/* In the plot's own stacking context, under its tooltip: a hover near an edge still reads. */}
              <EdgeFade left={vp.canLeft} right={!pinned} />
            </div>
            {noAnswer ? (
              <p
                className="crm-mut pointer-events-none absolute top-[38%] text-center text-[13px]"
                style={{ left: PLOT_LEFT, right: PLOT_RIGHT }}
              >
                {t("monitoring.chart.noAnswer")}
              </p>
            ) : null}
          </div>
        </div>
      </div>
      {/* Said, not drawn: the missing left fade already shows it. */}
      <p className="sr-only" aria-live="polite">
        {canScroll && !vp.canLeft ? t("monitoring.chart.historyEnd") : ""}
      </p>

      <div className="mt-3">
        <ViewStatsRow stats={drawn.stats} t={t} />
      </div>
      {range.end - range.start > DEFAULT_SPAN_MS ? (
        <div className="mt-4">
          <ChartNavigator
            monitorId={monitorId}
            range={range}
            checkMinutes={checkMinutes}
            liveKey={liveKey}
            from={from}
            span={span}
            onSeek={vp.seek}
            t={t}
          />
        </div>
      ) : null}
      <div className="mt-3">
        <ChartLegend t={t} />
      </div>
    </div>
  );
}
